import React, { createContext, useContext, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { filterService } from "../services/filterService";
import { sortService } from "../services/sortService";
import {
  getSortTypeFromParam,
  applyFiltersToCourses,
  createDefaultFilters,
  getSavedSortOrder,
  saveSortOrder,
  resetSortOrderStorage,
} from "./helpers/filterHelpers";
import { QUERY_KEYS } from "../constants";

const FilterContext = createContext();

/**
 * Custom hook to use the FilterContext
 * @returns {Object} The filter context value
 * @throws {Error} When used outside of a FilterProvider
 */
export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilterContext must be used within a FilterProvider");
  }
  return context;
};

/**
 * FilterProvider component that manages filtering and sorting state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} FilterProvider component
 */
export const FilterProvider = ({ children }) => {
  // Initialize with default values first, URL params will override if present
  const [filters, setFilters] = useState(() => {
    // Check URL params first, then fallback to localStorage
    const urlParams = new URLSearchParams(
      (window.location.hash || "").split("?")[1] || "",
    );
    const urlSortOrder = urlParams.get(QUERY_KEYS.sort);
    const sortOrder =
      urlSortOrder !== null
        ? parseInt(urlSortOrder, 10) || 0
        : getSavedSortOrder();

    return {
      ...createDefaultFilters(),
      sortOrder,
    };
  });

  /**
   * Updates the filters state with new values
   * @param {Object} updates - Object containing updates to apply to filters state
   * @param {boolean} skipLocalStorage - If true, skip saving to localStorage (e.g., when applying URL params)
   */
  const updateFilters = useCallback((updates, skipLocalStorage = false) => {
    setFilters((prev) => {
      const next = { ...prev, ...updates };
      if (
        skipLocalStorage ||
        !Object.prototype.hasOwnProperty.call(updates, "sortOrder")
      ) {
        return next;
      }
      saveSortOrder(next.sortOrder);
      return next;
    });
  }, []);

  /**
   * Applies current filters to a list of courses
   * @param {Array} courses - Array of courses to filter
   * @returns {Array} Filtered and sorted courses
   */
  const applyFilters = useCallback(
    (courses) => {
      return applyFiltersToCourses(
        courses,
        filters,
        filterService,
        sortService,
      );
    },
    [filters],
  );

  /**
   * Resets all filters to their default values
   */
  const resetFilters = useCallback(() => {
    setFilters((prev) => ({
      ...createDefaultFilters(),
      sortOrder: prev.sortOrder,
    }));
  }, []);

  const value = {
    filters,
    updateFilters,
    applyFilters,
    resetFilters,
    getSortTypeFromParam,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

FilterProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
